import { Suspense, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, LoaderIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form.tsx';
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperTrigger,
} from '@/components/ui/stepper';
import { cn } from '@/utils/utils.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useScrollToTopHTMLElement } from '@/hooks/use-scroll-to-top-html-element.ts';
import { useSession } from '@/services/providers/use-session.ts';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import {
  InviteUserFormData,
  inviteUserSchema,
  toInviteUserPayload,
} from '@/features/users/schemas/invite-user.schema.ts';
import { useInviteUserMutation } from '@/features/users/hooks/mutations/[user]/invite-user.ts';
import { CREATE_INVITE_USER_STEPS } from './create-invite-user-steps.tsx';

const INITIAL_STEP = 1;

const DEFAULT_VALUES: InviteUserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: undefined as unknown as InviteUserFormData['role'],
  plantIds: undefined,
  welcomeMessage: '',
};

export const CreateInviteUserForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { role: inviterRole } = useSession();
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const isLastStep = currentStep === CREATE_INVITE_USER_STEPS.length;
  const isFirstStep = currentStep === INITIAL_STEP;

  const { ref, scrollToTop } = useScrollToTopHTMLElement<HTMLDivElement>();

  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    reValidateMode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const { data: plants } = useGetPlantsQuery({ includeOverview: false });
  const inviteeRole = useWatch({ control: form.control, name: 'role' });

  const isInstallerInviterWithZeroPlants =
    inviterRole === 'Installer' &&
    (inviteeRole === 'Installer' || inviteeRole === 'Owner' || inviteeRole === 'Guest') &&
    plants !== undefined &&
    plants.length === 0;

  const resetForm = () => {
    setCurrentStep(INITIAL_STEP);
    form.reset(DEFAULT_VALUES);
  };

  const { mutate: inviteUser, isPending } = useInviteUserMutation({
    onSuccess: () => {
      onClose();
      resetForm();
    },
    onError: ({ emailFieldError }) => {
      if (emailFieldError) {
        form.setError('email', { type: 'server', message: emailFieldError });
        setCurrentStep(INITIAL_STEP);
      }
    },
  });

  const onSubmit = (data: InviteUserFormData) => {
    inviteUser(toInviteUserPayload(data));
  };

  const nextStep = async () => {
    const currentStepSchema = CREATE_INVITE_USER_STEPS[currentStep - 1].schema;
    const fieldsToValidate = Object.keys(
      currentStepSchema.shape
    ) as (keyof InviteUserFormData)[];

    const isValid = await form.trigger(fieldsToValidate);

    if (isValid && !isLastStep) {
      scrollToTop();
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Clear plantIds whenever the invitee role changes (skip the initial mount).
  const previousRoleRef = useRef<InviteUserFormData['role'] | undefined>(inviteeRole);
  useEffect(() => {
    if (previousRoleRef.current !== inviteeRole) {
      form.setValue('plantIds', undefined);
      form.clearErrors('plantIds');
      previousRoleRef.current = inviteeRole;
    }
  }, [inviteeRole, form]);

  const CurrentStepElement = CREATE_INVITE_USER_STEPS[currentStep - 1].Element;

  const isNextBlockedByEmptyPlants = currentStep === 2 && isInstallerInviterWithZeroPlants;
  const isSubmitBlockedByEmptyPlants = isLastStep && isInstallerInviterWithZeroPlants;

  return (
    <div ref={ref} className="max-h-[60vh] overflow-auto px-0.5">
      <CreateInviteUserFormStepper
        steps={CREATE_INVITE_USER_STEPS}
        currentStep={currentStep}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Suspense
            fallback={
              <div className="flex h-20 items-center justify-center">
                <LoadingSpinner size={50} />
              </div>
            }
          >
            <CurrentStepElement />
          </Suspense>

          <NavigationButtons
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isPending={isPending}
            disableNext={isNextBlockedByEmptyPlants}
            disableSubmit={isSubmitBlockedByEmptyPlants}
            onPrevious={prevStep}
            onNext={nextStep}
            submitLabel={t('users.invite.form.submit')}
            previousLabel={t('users.invite.form.previous')}
            nextLabel={t('users.invite.form.next')}
          />
        </form>
      </Form>
    </div>
  );
};

function NavigationButtons({
  isFirstStep,
  isLastStep,
  isPending,
  disableNext,
  disableSubmit,
  onPrevious,
  onNext,
  submitLabel,
  previousLabel,
  nextLabel,
}: {
  isFirstStep: boolean;
  isLastStep: boolean;
  isPending: boolean;
  disableNext: boolean;
  disableSubmit: boolean;
  onPrevious: () => void;
  onNext: () => void;
  submitLabel: string;
  previousLabel: string;
  nextLabel: string;
}) {
  return (
    <div className="flex justify-end">
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          className={cn('flex items-center gap-2', isFirstStep && 'hidden')}
          disabled={isPending}
        >
          <ArrowLeft size={16} />
          {previousLabel}
        </Button>

        <Button
          type="submit"
          className={cn(!isLastStep && 'hidden')}
          disabled={isPending || disableSubmit}
        >
          {isPending && (
            <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
        <Button
          type="button"
          onClick={onNext}
          className={cn('flex items-center gap-2', isLastStep && 'hidden')}
          disabled={isPending || disableNext}
        >
          {nextLabel}
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function CreateInviteUserFormStepper({
  steps,
  currentStep,
}: {
  steps: readonly { step: number }[];
  currentStep: number;
}) {
  return (
    <Stepper value={currentStep} className="items-start gap-4">
      {steps.map(({ step }) => (
        <StepperItem key={step} step={step} className="flex-1">
          <StepperTrigger asChild className="w-full flex-col items-start gap-2 rounded">
            <div>
              <StepperIndicator asChild className="bg-border h-1 w-full">
                <span className="sr-only">{step}</span>
              </StepperIndicator>
            </div>
          </StepperTrigger>
        </StepperItem>
      ))}
    </Stepper>
  );
}
